import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  BellRing,
  ChevronRight,
  ChevronsDownUp,
  ChevronsUpDown,
  ArrowUpRight,
  TrendingDown,
  ShieldCheck,
  Landmark,
  HardHat,
  ClipboardList,
  ArrowRight,
  AlertTriangle,
  MapPin,
  FileText,
  CheckCircle2,
  Building2,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { audFull, JOB_STATUS_META } from "@/lib/format";
import { useIsSmUp } from "@/hooks/useMediaQuery";

// ---------------------------------------------------------------------------
// Alert taxonomy shared by the dashboard
// severity: critical (money at risk / overdue) → warning (slipping) → info (routine action)
// category: where the alert belongs inside a site card
// ---------------------------------------------------------------------------
export const SEVERITY = { CRITICAL: "critical", WARNING: "warning", INFO: "info" };
export const CATEGORY = { FINANCIALS: "financials", OPERATIONS: "operations", ADMIN: "admin" };

const SEVERITY_ORDER = { critical: 0, warning: 1, info: 2 };

export const CATEGORIES = [
  { key: CATEGORY.FINANCIALS, label: "Financials", icon: Landmark, hint: "Overdue money, budgets, margins" },
  { key: CATEGORY.OPERATIONS, label: "Site Operations", icon: HardHat, hint: "Site issues and diary" },
  { key: CATEGORY.ADMIN, label: "Admin & Comms", icon: ClipboardList, hint: "Approvals, paperwork, client comms" },
];

const PILL_STYLES = {
  critical: "bg-red-500/90 text-white",
  warning: "bg-amber-400/90 text-stone-950",
  info: "bg-muted text-muted-foreground ring-1 ring-border",
};

const PILL_LABELS = { critical: "critical", warning: "warning", info: "routine" };

const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

/** Small round count pill — red / amber / neutral so priority reads at a glance. */
export const SeverityPill = ({ severity, count, testId, size = "sm" }) => {
  if (!count) return null;
  const dims = size === "sm" ? "h-5 min-w-[20px] px-1.5 text-[11px]" : "h-6 min-w-[24px] px-2 text-xs";
  return (
    <span
      className={`inline-flex shrink-0 items-center justify-center rounded-full font-semibold tabular-nums ${dims} ${PILL_STYLES[severity]}`}
      title={`${count} ${PILL_LABELS[severity]} alert${count === 1 ? "" : "s"}`}
      aria-label={`${count} ${PILL_LABELS[severity]} alert${count === 1 ? "" : "s"}`}
      data-testid={testId}
    >
      {count > 99 ? "99+" : count}
    </span>
  );
};

const countBySeverity = (items) =>
  items.reduce(
    (acc, it) => {
      acc[it.severity] = (acc[it.severity] || 0) + 1;
      return acc;
    },
    { critical: 0, warning: 0, info: 0 }
  );

// ---------------------------------------------------------------------------
// Sticky company-wide ticker — which build stages are bleeding across all jobs
// ---------------------------------------------------------------------------
export const BleedingStagesTicker = ({ stages = [], totalOver = 0 }) => {
  const slug = (s) => String(s.stage_code || "unstaged").replace(/[^a-zA-Z0-9_-]/g, "") || "unstaged";

  if (!stages.length) {
    return (
      <div
        className="flex items-center gap-2 rounded-lg border border-emerald-500/20 bg-emerald-500/[0.06] px-3 py-2 text-xs text-emerald-300"
        data-testid="stage-bleed-strip-healthy"
      >
        <ShieldCheck className="h-3.5 w-3.5 shrink-0" />
        <span className="shrink-0 whitespace-nowrap font-semibold uppercase tracking-wide">Stages on budget</span>
        <span className="min-w-0 truncate text-muted-foreground">— no cost centre is over its budget right now</span>
      </div>
    );
  }

  const total = totalOver || stages.reduce((s, x) => s + (x.over || 0), 0);

  return (
    <div className="sticky top-16 z-30 -mx-1 px-1 py-1.5 lg:top-0" data-testid="stage-bleed-sticky">
      <div
        className="rounded-lg border border-red-400/30 bg-card/95 px-3 py-2 shadow-[0_8px_24px_-12px_rgba(0,0,0,0.45)] backdrop-blur-md"
        data-testid="stage-bleed-strip"
        role="status"
        aria-live="polite"
      >
        <div className="flex items-center gap-3">
          <span className="flex shrink-0 items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-red-400">
            <TrendingDown className="h-3.5 w-3.5" /> Bleeding stages
          </span>
          <span className="hidden shrink-0 text-xs text-muted-foreground sm:inline" data-testid="stage-bleed-total">
            <span className="font-semibold text-red-400">{audFull(total)}</span> over · {plural(stages.length, "stage")} company-wide
          </span>
          <div className="flex min-w-0 flex-1 items-center gap-2 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {stages.map((s) => (
              <span
                key={s.stage_code || "unstaged"}
                className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-red-400/30 bg-background/60 px-2.5 py-0.5 text-[11px]"
                data-testid={`stage-bleed-chip-${slug(s)}`}
              >
                <span className="font-semibold">{s.stage_name}</span>
                <span className="font-semibold text-red-400">{audFull(s.over)} over</span>
                {s.pct_over > 0 && <span className="text-muted-foreground">· {Math.round(s.pct_over)}%</span>}
              </span>
            ))}
          </div>
          <Link
            to="/financials?view=bva"
            className="hidden shrink-0 items-center gap-1 text-xs font-medium text-primary hover:underline sm:flex"
            data-testid="stage-bleed-review-link"
          >
            Review <ArrowRight className="h-3 w-3" />
          </Link>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Lifecycle sections — the board mirrors the Jobs page buckets
// ---------------------------------------------------------------------------
export const BOARD_SECTIONS = [
  {
    key: "active",
    label: "Active",
    hint: "In progress on site",
    statuses: ["in_progress", "on_hold"],
    icon: HardHat,
    empty: "All clear — nothing needs you on the tools right now.",
  },
  {
    key: "quoting",
    label: "Quoting",
    hint: "Being priced or waiting to start",
    statuses: ["quoting", "scheduled"],
    icon: FileText,
    empty: "All clear — nothing outstanding in the pipeline.",
  },
  {
    key: "completed",
    label: "Completed",
    hint: "Finished projects",
    statuses: ["completed"],
    icon: CheckCircle2,
    empty: "All clear — no loose ends on finished jobs.",
  },
];
const GENERAL_SECTION = {
  key: "general",
  label: "Company-wide",
  hint: "Alerts that aren't tied to one site",
  icon: Building2,
};

const sectionForStatus = (status) =>
  (BOARD_SECTIONS.find((sec) => sec.statuses.includes(status)) || BOARD_SECTIONS[1]).key;

/** Red / amber count of the urgent (critical + warning) alerts on a site. */
export const RiskBadge = ({ counts, testId }) => {
  const urgent = (counts.critical || 0) + (counts.warning || 0);
  if (!urgent) {
    return (
      <span
        className="inline-flex h-5 shrink-0 items-center gap-1 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2 text-[10px] font-semibold uppercase tracking-wide text-emerald-300"
        data-testid={testId}
        data-level="clear"
        title="No urgent alerts — routine actions only"
      >
        <ShieldCheck className="h-3 w-3" /> Routine
      </span>
    );
  }
  const critical = counts.critical > 0;
  return (
    <span
      className={`inline-flex h-5 shrink-0 items-center gap-1 rounded-full px-2 text-[11px] font-semibold tabular-nums ${
        critical ? "bg-red-500/90 text-white" : "bg-amber-400/90 text-stone-950"
      }`}
      data-testid={testId}
      data-level={critical ? "critical" : "warning"}
      title={`${plural(counts.critical, "critical alert")} · ${plural(counts.warning, "warning")}`}
    >
      <AlertTriangle className="h-3 w-3" />
      {urgent} urgent
    </span>
  );
};

// ---------------------------------------------------------------------------
// One collapsible site card — collapsed: name · address · risk badge;
// expanded: alerts grouped by Financials / Site Operations / Admin & Comms
// ---------------------------------------------------------------------------
const SiteAlertCard = ({ group, open, onOpenChange }) => {
  const key = group.jobId || "general";
  const counts = countBySeverity(group.items);
  const total = group.items.length;
  const job = group.job;

  // Category buckets in fixed order, each sorted worst-first
  const buckets = CATEGORIES.map((cat) => ({
    ...cat,
    items: group.items
      .filter((it) => it.category === cat.key)
      .sort((a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity]),
  })).filter((b) => b.items.length > 0);

  const summary = buckets.map((b) => `${b.label} ${b.items.length}`).join(" · ");
  const tone =
    counts.critical > 0
      ? "border-red-400/30 bg-red-500/[0.04]"
      : counts.warning > 0
        ? "border-amber-400/25 bg-amber-400/[0.03]"
        : "border-border/70 bg-muted/20";
  const statusMeta = job?.status ? JOB_STATUS_META[job.status] : null;
  const showStatus = statusMeta && (job.status === "on_hold" || job.status === "scheduled");

  return (
    <Collapsible open={open} onOpenChange={onOpenChange} asChild>
      <div
        className={`min-w-0 rounded-lg border p-1.5 transition-colors ${tone}`}
        data-testid={`attention-job-box-${key}`}
        data-open={open ? "true" : "false"}
        data-critical={counts.critical}
        data-updated-at={job?.updated_at || undefined}
      >
        <div className="flex items-start gap-1">
          <CollapsibleTrigger asChild>
            <button
              className="group/site flex min-w-0 flex-1 items-start gap-2 rounded-md px-2 py-1.5 text-left transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              aria-label={`${open ? "Collapse" : "Expand"} ${group.jobName || "Company-wide"}`}
              aria-expanded={open}
              data-testid={`attention-job-box-toggle-${key}`}
            >
              <ChevronRight
                className={`mt-0.5 h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 ${open ? "rotate-90" : ""}`}
              />
              <span className="min-w-0 flex-1">
                <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <span
                    className={`truncate text-xs font-semibold uppercase tracking-wide ${group.jobId ? "text-primary" : "text-muted-foreground"}`}
                    data-testid={`attention-job-box-title-${key}`}
                  >
                    {group.jobId ? group.jobName || job?.name || "Job" : "Company-wide"}
                  </span>
                  {showStatus && (
                    <Badge variant="outline" className={`h-4 rounded-full px-1.5 text-[9px] ${statusMeta.cls}`} data-testid={`attention-job-box-status-${key}`}>
                      {statusMeta.label}
                    </Badge>
                  )}
                </span>
                <span className="mt-0.5 flex min-w-0 items-center gap-1 text-[11px] text-muted-foreground" data-testid={`attention-job-box-address-${key}`}>
                  {group.jobId ? (
                    <>
                      <MapPin className="h-3 w-3 shrink-0 opacity-70" />
                      <span className="truncate">{job?.address || job?.client_name || "No address yet"}</span>
                    </>
                  ) : (
                    <span className="truncate">Cash, cost variance and other alerts that aren't tied to one site</span>
                  )}
                </span>
                {!open && (
                  <span className="mt-0.5 block truncate text-[11px] text-muted-foreground/80" data-testid={`attention-job-box-summary-${key}`}>
                    {summary}
                  </span>
                )}
              </span>
              <span className="ml-1 flex shrink-0 flex-col items-end gap-1 pt-0.5">
                <RiskBadge counts={counts} testId={`attention-job-box-risk-${key}`} />
                <span className="text-[10px] tabular-nums text-muted-foreground" data-testid={`attention-job-box-total-${key}`}>
                  {plural(total, "alert")}
                </span>
              </span>
            </button>
          </CollapsibleTrigger>
          {group.jobId && (
            <Link
              to={`/jobs/${group.jobId}`}
              className="mt-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-primary"
              title="Open job"
              aria-label={`Open ${group.jobName || "job"}`}
              data-testid={`attention-job-box-open-${key}`}
            >
              <ArrowUpRight className="h-4 w-4" />
            </Link>
          )}
        </div>

        <CollapsibleContent>
          <div className="space-y-2 pb-1 pt-1" data-testid={`attention-job-box-body-${key}`}>
            {buckets.map((b) => (
              <div key={b.key} data-testid={`attention-category-${key}-${b.key}`}>
                <p className="flex items-center gap-1.5 px-3 pb-1 pt-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground/80">
                  <b.icon className="h-3 w-3" />
                  {b.label}
                  <span className="ml-0.5 rounded-full bg-muted px-1.5 text-[10px] tabular-nums text-muted-foreground">{b.items.length}</span>
                </p>
                <div className="space-y-0.5">{b.items.map((it) => it.node)}</div>
              </div>
            ))}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
};

// ---------------------------------------------------------------------------
// One labelled lifecycle section: header strip + greedy two-column packing
// ---------------------------------------------------------------------------
const BoardSection = ({ section, groups, isOpen, setOpen, twoColumns }) => {
  const totals = countBySeverity(groups.flatMap((g) => g.items));
  const totalCount = totals.critical + totals.warning + totals.info;

  // Greedy two-column packing (sm and up) so a tall expanded site never leaves a
  // hole beside a folded one. Cards still read in recency order, left → right.
  // On phones it's one column in plain recency order.
  const estimateHeight = (g) => {
    if (!isOpen(g)) return 1.6;
    const cats = new Set(g.items.map((it) => it.category)).size;
    return 1.4 + cats * 0.7 + g.items.reduce((s, it) => s + (it.weight || 1), 0);
  };
  const columns = [[], []];
  const heights = [0, 0];
  groups.forEach((g) => {
    const col = twoColumns && heights[0] > heights[1] ? 1 : 0;
    columns[col].push(g);
    heights[col] += estimateHeight(g);
  });

  const Icon = section.icon;
  return (
    <section className="min-w-0" data-testid={`attention-section-${section.key}`} data-count={groups.length} aria-label={`${section.label} sites`}>
      <div className="mb-2 flex flex-wrap items-center gap-x-2 gap-y-1 border-b border-border/60 pb-1.5">
        <h3 className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-foreground/90" data-testid={`attention-section-title-${section.key}`}>
          <Icon className="h-3.5 w-3.5 text-primary" />
          {section.label}
        </h3>
        <Badge variant="outline" className="h-4 rounded-full border-border px-1.5 text-[10px] tabular-nums text-muted-foreground" data-testid={`attention-section-sites-${section.key}`}>
          {groups.length === 0 ? "0 sites" : plural(groups.length, section.key === "general" ? "group" : "site")}
        </Badge>
        {totalCount > 0 && (
          <span className="flex items-center gap-1" data-testid={`attention-section-pills-${section.key}`}>
            <SeverityPill severity="critical" count={totals.critical} />
            <SeverityPill severity="warning" count={totals.warning} />
            <SeverityPill severity="info" count={totals.info} />
          </span>
        )}
        <span className="ml-auto hidden text-[10px] text-muted-foreground sm:inline">{section.hint}</span>
      </div>
      {groups.length === 0 ? (
        <p className="flex items-center gap-1.5 px-1 py-1 text-[11px] text-muted-foreground" data-testid={`attention-section-empty-${section.key}`}>
          <ShieldCheck className="h-3.5 w-3.5 text-emerald-400/80" /> {section.empty}
        </p>
      ) : (
        <div className="grid grid-cols-1 items-start gap-3 sm:grid-cols-2" data-testid={`attention-section-grid-${section.key}`}>
          <div className="flex min-w-0 flex-col gap-3">
            {columns[0].map((g) => (
              <SiteAlertCard key={g.key} group={g} open={isOpen(g)} onOpenChange={(v) => setOpen(g, v)} />
            ))}
          </div>
          {twoColumns && (
            <div className="flex min-w-0 flex-col gap-3">
              {columns[1].map((g) => (
                <SiteAlertCard key={g.key} group={g} open={isOpen(g)} onOpenChange={(v) => setOpen(g, v)} />
              ))}
            </div>
          )}
        </div>
      )}
    </section>
  );
};

// ---------------------------------------------------------------------------
// The board: header with totals + expand/collapse all, then Active → Quoting →
// Completed sections (plus Company-wide). Every card starts collapsed; within a
// section, the most recently modified job comes first.
// ---------------------------------------------------------------------------
export const AttentionBoard = ({ groups, jobsIndex = [] }) => {
  // user overrides on top of the default (everything folded until you open it)
  const [overrides, setOverrides] = useState({});
  const twoColumns = useIsSmUp();

  const jobsById = useMemo(() => {
    const m = new Map();
    (jobsIndex || []).forEach((j) => m.set(j.id, j));
    return m;
  }, [jobsIndex]);

  const sections = useMemo(() => {
    const enriched = groups.map((g) => {
      const job = g.jobId ? jobsById.get(g.jobId) || null : null;
      return {
        ...g,
        key: g.jobId || "general",
        job,
        jobName: g.jobName || job?.name || "",
        counts: countBySeverity(g.items),
        updatedAt: job?.updated_at || "",
      };
    });
    const byRecency = (a, b) =>
      (b.updatedAt || "").localeCompare(a.updatedAt || "") ||
      b.counts.critical - a.counts.critical ||
      b.items.length - a.items.length;
    const general = enriched.filter((g) => !g.jobId);
    const sited = enriched.filter((g) => g.jobId);
    const out = BOARD_SECTIONS.map((sec) => ({
      section: sec,
      groups: sited.filter((g) => sectionForStatus(g.job?.status || "quoting") === sec.key).sort(byRecency),
    }));
    if (general.length) out.push({ section: GENERAL_SECTION, groups: general });
    return out;
  }, [groups, jobsById]);

  const all = useMemo(() => sections.flatMap((s) => s.groups), [sections]);
  const totals = useMemo(() => countBySeverity(groups.flatMap((g) => g.items)), [groups]);
  const totalCount = totals.critical + totals.warning + totals.info;
  if (totalCount === 0) return null;

  const isOpen = (g) => overrides[g.key] ?? false;
  const setOpen = (g, v) => setOverrides((prev) => ({ ...prev, [g.key]: v }));
  const setAll = (value) => setOverrides(Object.fromEntries(all.map((g) => [g.key, value])));
  const openCount = all.filter(isOpen).length;

  return (
    <Card className="border-l-2 border-l-primary shadow-sm" data-testid="dashboard-attention-card">
      <CardHeader className="pb-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="flex flex-wrap items-center gap-2 text-sm font-semibold">
            <BellRing className="h-4 w-4 text-primary" /> Needs attention
            <Badge variant="outline" className="border-primary/40 bg-accent text-primary" data-testid="attention-total-badge">
              {totalCount}
            </Badge>
            <span className="flex items-center gap-1.5 text-[11px] font-normal text-muted-foreground" data-testid="attention-severity-summary">
              {totals.critical > 0 && (
                <span className="flex items-center gap-1">
                  <SeverityPill severity="critical" count={totals.critical} /> critical
                </span>
              )}
              {totals.warning > 0 && (
                <span className="flex items-center gap-1">
                  <SeverityPill severity="warning" count={totals.warning} /> warning
                </span>
              )}
              {totals.info > 0 && (
                <span className="flex items-center gap-1">
                  <SeverityPill severity="info" count={totals.info} /> routine
                </span>
              )}
            </span>
          </CardTitle>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              className="h-7 gap-1 text-xs text-muted-foreground hover:text-foreground"
              onClick={() => setAll(true)}
              disabled={openCount === all.length}
              data-testid="attention-expand-all"
            >
              <ChevronsUpDown className="h-3.5 w-3.5" /> Expand all
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 gap-1 text-xs text-muted-foreground hover:text-foreground"
              onClick={() => setAll(false)}
              disabled={openCount === 0}
              data-testid="attention-collapse-all"
            >
              <ChevronsDownUp className="h-3.5 w-3.5" /> Collapse all
            </Button>
          </div>
        </div>
        <p className="text-[11px] text-muted-foreground">
          Grouped by lifecycle, most recently updated first. Tap a site to open its Financials, Site Operations and Admin & Comms alerts.
        </p>
      </CardHeader>
      <CardContent className="space-y-5 pb-4" data-testid="attention-board-grid">
        {sections.map(({ section, groups: secGroups }) => (
          <BoardSection key={section.key} section={section} groups={secGroups} isOpen={isOpen} setOpen={setOpen} twoColumns={twoColumns} />
        ))}
      </CardContent>
    </Card>
  );
};
